import React, {
    createContext,
    useContext,
    useEffect,
    useReducer,
    useRef
} from 'react';

import { useWatchlistNotificationTrigger } from '../components/WatchlistNotification/watchlistnotification-context';
import { localStorageGet } from '../utils/local-storage';
import { callApi, watchlistActions } from '../utils/watchlist-actions';
import {
    ADD_SELECTED_WATCHLIST_FUNDS,
    ADD_WATCHLIST_FUND,
    CREATE_NEW_WATCHLIST,
    REMOVE_WATCHLIST_FUND,
    REMOVE_SELECTED_WATCHLIST_FUNDS,
    RENAME_WATCHLIST,
    SELECT_WATCHLIST,
    SET_FETCHED_WATCHLIST,
    SET_LOCAL_STORAGE_WATCHLIST,
    UPDATE_AFTER_POST
} from '../utils/watchlist-settings';
import { sortWatchlists } from '../utils/watchlist-utils';

const LS_KEY = 'trp-watchlist';

const getWatchlistById = (watchlists, watchlistId) =>
    watchlists && watchlists.find((wl) => wl.watchlistUuid === watchlistId);
// hook that allows comparison of the previous value
const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

const getFromLS = () => {
    // Unauthenticated local storage scenario
    try {
        const lsWatchlist = localStorageGet(LS_KEY);
        return lsWatchlist || [];
    } catch (error) {
        return [];
    }
};

const toArray = (v) => (Array.isArray(v) ? v : Array.of(v));
const getFundsValue = (f) => (typeof f === 'string' ? f : f.ticker);
const isFundNotInWatchlist = (watchlist) => (fund) =>
    watchlist.every((f) => getFundsValue(f) !== getFundsValue(fund));

const addFundForGuest = (state, newFunds) => {
    const fundsToAdd = newFunds.filter(isFundNotInWatchlist(state.tickers));
    return {
        ...state,
        tickers: state.tickers.concat(fundsToAdd),
        isAddingFund: fundsToAdd.length > 0,
        numberFundsAdded: fundsToAdd.length,
        isRemovingFund: false
    };
};

const addFundForUser = (state, newFunds, selectedWatchlist) => {
    const fundsToAdd = newFunds
        .filter(isFundNotInWatchlist(selectedWatchlist.items))
        .map((fund) => ({ ticker: fund }));

    const updatedWatchlists = {
        ...selectedWatchlist,
        items: selectedWatchlist.items.concat(fundsToAdd)
    };

    return {
        ...state,
        name: updatedWatchlists.displayName,
        selectedId: updatedWatchlists.watchlistUuid,
        watchlists: state.watchlists.map(
            (watchlist) =>
                watchlist.watchlistUuid === updatedWatchlists.watchlistUuid
                    ? updatedWatchlists
                    : watchlist
        ),
        tickers: updatedWatchlists.items.map(getFundsValue),
        isAddingFund: fundsToAdd.length > 0,
        numberFundsAdded: fundsToAdd.length,
        isRemovingFund: false
    };
};

const watchlistReducer = (
    state,
    {
        fetchedWatchlist,
        funds,
        lsWatchlist,
        newWatchlist,
        postResponse,
        selectedFunds,
        selectedWatchlist,
        ticker,
        type
    }
) => {
    const { tickers: watchlistFunds, watchlists } = state;

    switch (type) {
        case ADD_SELECTED_WATCHLIST_FUNDS:
        case ADD_WATCHLIST_FUND: {
            const newFunds = toArray(ticker || funds || selectedFunds);

            return selectedWatchlist
                ? addFundForUser(state, newFunds, selectedWatchlist)
                : addFundForGuest(state, newFunds);
        }
        case CREATE_NEW_WATCHLIST: {
            const { displayName, items = [], watchlistUuid } = newWatchlist;
            return {
                ...state,
                name: displayName,
                selectedId: watchlistUuid,
                tickers: items.map((f) => f.ticker),
                watchlists: [...state.watchlists, newWatchlist].sort(
                    sortWatchlists
                ),
                numberFundsAdded: items.length,
                isAddingFund: true,
                isRemovingFund: false
            };
        }
        case REMOVE_SELECTED_WATCHLIST_FUNDS:
        case REMOVE_WATCHLIST_FUND: {
            const isMatchingFund = (watchlistFund) => {
                if (selectedFunds)
                    return selectedFunds.some((f) => f === watchlistFund);
                if (Array.isArray(ticker))
                    return ticker.some((f) => f.includes(watchlistFund));
                if (Array.isArray(funds))
                    return funds.some((f) => f.includes(watchlistFund));
                return ticker === watchlistFund;
            };
            const filterWatchlistFunds = (watchlist) =>
                watchlist.filter((f) => !isMatchingFund(getFundsValue(f)));
            const updateWatchlistItems = (watchlist) => ({
                ...watchlist,
                items: filterWatchlistFunds(watchlist.items)
            });
            const updatedTickerState = {
                ...state,
                tickers: filterWatchlistFunds(watchlistFunds)
            };

            if (!selectedWatchlist) {
                return {
                    ...updatedTickerState,
                    isAddingFund: false,
                    isRemovingFund: true
                };
            }
            const {
                displayName,
                watchlistUuid: selectedWatchlistUuid
            } = selectedWatchlist;

            const updatedWatchlists = watchlists.map(
                (ele) =>
                    ele.watchlistUuid !== selectedWatchlistUuid
                        ? ele
                        : updateWatchlistItems(ele)
            );

            return {
                ...updatedTickerState,
                name: displayName,
                selectedId: selectedWatchlistUuid,
                watchlists: updatedWatchlists,
                isAddingFund: false,
                isRemovingFund: true
            };
        }

        case RENAME_WATCHLIST: {
            const updatedWatchlists = watchlists.map(
                (watchlist) =>
                    watchlist.watchlistUuid === postResponse.watchlistUuid
                        ? { ...watchlist, ...postResponse }
                        : watchlist
            );
            const { displayName } = postResponse;
            return {
                ...state,
                name: displayName,
                watchlists: updatedWatchlists.sort(sortWatchlists)
            };
        }
        case SELECT_WATCHLIST: {
            const { watchlistUuid: selectedWatchlistUuid } = selectedWatchlist;
            const { displayName, items, watchlistUuid } =
                getWatchlistById(watchlists, selectedWatchlistUuid) || {};

            return {
                ...state,
                name: displayName,
                selectedId: watchlistUuid,
                tickers: items.map(({ ticker: t }) => t),
                isAddingFund: false,
                isRemovingFund: false
            };
        }
        case SET_FETCHED_WATCHLIST: {
            if (fetchedWatchlist.length === 0) {
                return {
                    name: 'My Watchlist 1',
                    selectedId: '',
                    tickers: [],
                    watchlists: []
                };
            }
            fetchedWatchlist.sort(sortWatchlists);
            const initialWatchlistFromServer =
                fetchedWatchlist.length && fetchedWatchlist[0];
            return {
                name:
                    (initialWatchlistFromServer &&
                        initialWatchlistFromServer.displayName) ||
                    'My Watchlist 1',
                selectedId:
                    (initialWatchlistFromServer &&
                        initialWatchlistFromServer.watchlistUuid) ||
                    '',
                tickers:
                    (initialWatchlistFromServer &&
                        initialWatchlistFromServer.items.map(
                            ({ ticker: t }) => t
                        )) ||
                    [],
                watchlists: fetchedWatchlist.sort(sortWatchlists) || []
            };
        }
        case SET_LOCAL_STORAGE_WATCHLIST: {
            return {
                name: 'My Watchlist',
                selectedId: '',
                tickers: lsWatchlist,
                watchlists: []
            };
        }
        case UPDATE_AFTER_POST: {
            const updatedWatchlists = watchlists.map(
                (watchlist) =>
                    watchlist.watchlistUuid === postResponse.watchlistUuid
                        ? { ...watchlist, ...postResponse }
                        : watchlist
            );

            const { httpStatusCode, displayName } = postResponse;

            if (httpStatusCode === 500) return state;
            return {
                ...state,
                name: displayName,
                watchlists: updatedWatchlists.sort(sortWatchlists)
            };
        }
        default: {
            throw new Error();
        }
    }
};

const useWatchlist = (loggedIn, pushWatchlistNotification) => {
    const initialWatchlist = loggedIn
        ? {
              name: 'My Watchlist 1',
              selectedId: '',
              tickers: [],
              watchlists: [],
              isAddingFund: false
          }
        : {
              name: 'My Watchlist',
              tickers: getFromLS(),
              isAddingFund: false
          };
    const [
        {
            name,
            selectedId,
            tickers: watchlistFunds,
            watchlists,
            isAddingFund,
            isRemovingFund,
            numberFundsAdded
        },
        dispatch
    ] = useReducer(watchlistReducer, initialWatchlist);
    const previousWatchlist = usePrevious(watchlistFunds);
    const previousWatchlists = usePrevious(watchlists);
    const dispatchWatchlistNotification = useWatchlistNotificationTrigger();
    async function fetchWatchlists() {
        try {
            const result = await callApi(watchlistActions.GET_WATCHLISTS);
            const response = await result.json();
            dispatch({
                type: SET_FETCHED_WATCHLIST,
                fetchedWatchlist: response
            });
            return response; // return watchlists to determine naming/replacing pattern
        } catch (err) {
            throw err;
        }
    }

    async function createNewWatchlist(items = [], newName) {
        let newWatchlistName = '';
        const watchlist1Exists = watchlists.some(
            (wl) => wl.displayName === 'My Watchlist 1'
        );
        if (newName) {
            newWatchlistName = newName;
        } else if (watchlist1Exists) {
            newWatchlistName = `My Watchlist ${watchlists.length + 1}`;
        } else {
            newWatchlistName = 'My Watchlist 1';
        }
        const body = {
            displayName: newWatchlistName,
            items: items.map(
                (ticker) => (typeof ticker === 'string' ? { ticker } : ticker)
            )
        };
        try {
            const result = await callApi(
                watchlistActions.CREATE_NEW_WATCHLIST,
                { body }
            );
            const response = await result.json();
            dispatch({
                type: CREATE_NEW_WATCHLIST,
                newWatchlist: response
            });
        } catch (err) {
            throw err;
        }
    }

    async function deleteWatchlist(uuid, newWatchlistInfo) {
        try {
            await callApi(watchlistActions.DELETE_WATCHLIST, { id: uuid });
            const watchLists = await fetchWatchlists();
            if (watchLists.length === 0) {
                // create new watchlist on server
                const { items = [], wName = 'My Watchlist 1' } =
                    newWatchlistInfo || {};
                createNewWatchlist(items, wName);
            }
        } catch (err) {
            throw err;
        }
    }

    // Fetch initial watchlist and retrieve watchlist from server or LS when login status changes
    useEffect(
        () => {
            if (loggedIn) {
                (async () => {
                    await fetchWatchlists();
                })();
            }
        },
        [loggedIn]
    );

    // Set watchlist on server or LS when watchlistFunds changes
    useEffect(
        () => {
            // Set in local storage for unauthenticated
            // if (!loggedIn) {
            //     localStorageSet(LS_KEY, watchlistFunds);
            // } else if (watchlists) {
            // don't run this when creating a watchlist
            if (selectedId) {
                const currentWatchlist = getWatchlistById(
                    watchlists,
                    selectedId
                );

                // Only update if current Watchlist exists
                if (currentWatchlist) {
                    (async () => {
                        try {
                            const body = {
                                displayName: name,
                                watchlistUuid: selectedId,
                                items: currentWatchlist.items.map(
                                    (fund) =>
                                        typeof fund === 'string'
                                            ? { ticker: fund }
                                            : fund
                                )
                            };

                            const result = await callApi(
                                watchlistActions.UPDATE_WATCHLIST,
                                { id: selectedId, body }
                            );
                            // need to strip out null createdTimestamp from the postResponse
                            const {
                                createdTimestamp,
                                ...response
                            } = await result.json();
                            dispatch({
                                type: UPDATE_AFTER_POST,
                                postResponse: response
                            });
                        } catch (e) {
                            throw e;
                        }
                    })();
                }
            }
        },
        [watchlistFunds]
    );

    // Dispatch notification to show 'add to watchlist' or 'removed from watchlist' notification
    useEffect(
        () => {
            const selectedWatchListChanged =
                previousWatchlist &&
                selectedId !== previousWatchlist.selectedId;
            const watchlistChangeOnly =
                selectedWatchListChanged && !isAddingFund && !isRemovingFund;
            const guestWatchlistUpdate =
                previousWatchlist &&
                previousWatchlist.length !== watchlistFunds.length &&
                selectedId === ''; // In the guest experience selectedId is never set after initialization
            // Check if funds were added to a new watchlist
            const addedToNewWatchlist =
                watchlists &&
                previousWatchlists &&
                watchlists.length &&
                previousWatchlists.length &&
                watchlists.length > previousWatchlists.length &&
                getWatchlistById(watchlists, selectedId) &&
                getWatchlistById(watchlists, selectedId).modifiedTimestamp ===
                    null;
            const loggedInWatchlistUpdated =
                (loggedIn &&
                    !guestWatchlistUpdate &&
                    selectedWatchListChanged &&
                    (previousWatchlists && previousWatchlists.length > 0) &&
                    getWatchlistById(previousWatchlists, selectedId) &&
                    getWatchlistById(watchlists, selectedId)) ||
                addedToNewWatchlist;

            if (
                !watchlistChangeOnly &&
                (guestWatchlistUpdate || loggedInWatchlistUpdated)
            ) {
                dispatchWatchlistNotification({
                    display: true,
                    watchlistName: name,
                    addingFundMsg: isAddingFund,
                    addingNewWatchlist: addedToNewWatchlist,
                    amountOfFundsAdded: numberFundsAdded
                });
            }
        },
        [watchlistFunds]
    );

    return [
        {
            name,
            selectedId,
            watchlistFunds,
            watchlists,
            pushWatchlistNotification
        },
        dispatch,
        { deleteWatchlist, createNewWatchlist }
    ];
};

const WatchlistStateContext = createContext();
const WatchlistDispatchContext = createContext();
const WatchlistActionsContext = createContext();

export function WatchlistProvider({
    loggedIn,
    pushWatchlistNotification,
    children
}) {
    const [state, dispatch, watchlistApiActions] = useWatchlist(
        loggedIn,
        pushWatchlistNotification
    );
    return (
        <WatchlistStateContext.Provider value={state}>
            <WatchlistDispatchContext.Provider value={dispatch}>
                <WatchlistActionsContext.Provider value={watchlistApiActions}>
                    {children}
                </WatchlistActionsContext.Provider>
            </WatchlistDispatchContext.Provider>
        </WatchlistStateContext.Provider>
    );
}

export function useWatchlistState() {
    const context = useContext(WatchlistStateContext);
    if (context === undefined) {
        throw new Error(
            'useWatchlistState must be used within a WatchlistProvider'
        );
    }
    return context;
}

export function useWatchlistDispatch() {
    const context = useContext(WatchlistDispatchContext);
    if (context === undefined) {
        throw new Error(
            'useWatchlistDispatch must be used within a WatchlistProvider'
        );
    }
    return context;
}

export function useWatchlistActionsApi() {
    const context = useContext(WatchlistActionsContext);
    if (context === undefined) {
        throw new Error(
            'useWatchlistActionsApi must be used within a WatchlistProvider'
        );
    }
    return context;
}

export function WatchlistConsumer({ children }) {
    return (
        <WatchlistStateContext.Consumer>
            {(watchListFunds) => (
                <WatchlistDispatchContext.Consumer>
                    {(dispatch) => children({ watchListFunds, dispatch })}
                </WatchlistDispatchContext.Consumer>
            )}
        </WatchlistStateContext.Consumer>
    );
}

export function renameWatchlist(watchlistUuid, watchlistName) {
    const { watchlists } = useContext(WatchlistStateContext);
    const dispatch = useContext(WatchlistDispatchContext);

    const watchlist = getWatchlistById(watchlists || [], watchlistUuid);

    if (watchlist) {
        const body = {
            ...watchlist,
            displayName: watchlistName
        };

        return async () => {
            try {
                const result = await callApi(
                    watchlistActions.RENAME_WATCHLIST,
                    {
                        id: watchlistUuid,
                        body
                    }
                );
                // need to strip out null createdTimestamp from the postResponse
                const {
                    createdTimestamp,
                    ...postResponse
                } = await result.json();
                return dispatch({
                    type: UPDATE_AFTER_POST,
                    postResponse
                });
            } catch (err) {
                throw new Error(err);
            }
        };
    }
    return null;
}

export default useWatchlist;
